import {
  ALIXPARTNERS,
  ALIXPARTNERS_APPROVER,
  CLIENT_DATA_UPLOADER,
  CLIENT_REPORT_VIEWER,
} from '../constants/etsMailRoles';

import mailtoLink from 'mailto-link';

export const generateEnvETSMail = (
  service: string,
  codeName: string,
  userName: string
) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: Deploy ${service} for ${codeName} environment`;
  const body: string = `Dear ETS,

Please deploy the following service to the ${codeName} ToolBuilder environment. The url to access this environment is: ${window.location.hostname}

ETS please:
- Run a ToolBuilder release to the ${codeName} stage with the correct release variable set for this service
- A full list of of Release Variables can be found here on Confluence https://alixpartners.atlassian.net/wiki/spaces/CDO/pages/878477843/3.1.+Environment+setup

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateETSMail = (
  codeName: string,
  userName: string,
  emails: string[],
  role: string
) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: New user request for ${codeName} environment`;
  let body: string = `Dear ETS,

Please extend access for the following persons to the ${codeName} ToolBuilder environment. The url to access this environment is ${window.location.hostname}
`;

  const alixPartnersRoleBodyPartial: string = `
The following AlixPartners colleagues should get the Developer role.
They should be added to the Sec_${codeName}_Developer AD Group.
`;

  const alixPartnersApproverRoleBodyPartial: string = `
The following AlixPartners colleagues should get the Engagement Lead role.
They should be added to the Sec_${codeName}_AZ_EL AD Group.

Note : Persons in this group with this role are able to publish Reports with clients. They must NOT also have the Developer role. Please confirm before adding them to the Engagement Lead group.
`;

  const clientDataRoleBodyPartial: string = `
The following client staff should get the External Analyst role. They will need @ets.alixpartners.com accounts
created and communicated to the below emails.
Their @ets.alixpartners.com accounts should be added to the Sec_${codeName}_External_Analyst AD Group.
`;

  const clientReportRoleBodyPartial: string = `
The following client staff should get the External Stakeholder role. They will need @ets.alixpartners.com accounts
created and communicated to the below emails.
Their @ets.alixpartners.com accounts should be added to the Sec_${codeName}_External_Stakeholder AD Group.
`;

  if (role === ALIXPARTNERS) {
    body += alixPartnersRoleBodyPartial;
  } else if (role === ALIXPARTNERS_APPROVER) {
    body += alixPartnersApproverRoleBodyPartial;
  } else if (role === CLIENT_DATA_UPLOADER) {
    body += clientDataRoleBodyPartial;
  } else if (role === CLIENT_REPORT_VIEWER) {
    body += clientReportRoleBodyPartial;
  }

  emails.forEach((email) => {
    body += `
- ${email}`;
  });

  body += `

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateHelpMail = (userName: string) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string =
    'AlixIP ToolBuilder: Request for access to CDO Confluence Space';
  const body: string = `Dear ETS,

Please grant me access to the CDO Office space on Confluence. I specifically require access to the ToolBuilder help page at this location:
https://alixpartners.atlassian.net/wiki/spaces/CDO/pages/893288563/ToolBuilder+Help

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateShareIdeasMail = (userName: string) => {
  const to: string = 'CDO-General@groups.alixpartners.com';
  const subject: string = 'AlixIP ToolBuilder: Ideas and feedback';
  const body: string = `Dear CDO,

I wanted to share the following ideas/feedback with you...

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateSlackAccessMail = (userName: string) => {
  const to: string = 'CDO-General@groups.alixpartners.com';
  const subject: string = 'AlixIP ToolBuilder: Request for access to Slack';
  const body: string = `Dear CDO,

Please grant me access to the Slack #tool-builder channel. I want to be involved in the conversation!

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateEtsVdiMail = (userName: string, codeName: string) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = 'AlixIP ToolBuilder: Request for ETS VDI Desktop';
  const body: string = `Dear ETS,

I am using ToolBuilder and I need an ETS VDI Desktop. Please configure one for me.

I am using the ${codeName} ToolBuilder environment. The url to access this environment is: ${window.location.hostname}

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateShareGlobalscapeMail = (
  userName: string,
  globalscapeUrl: string
) => {
  const to: string = '';
  const subject: string =
    'AlixPartners is now ready to receive your data via Globalscape';
  const body: string = `Dear ,

We are now ready to receive your data securely using Globalscape. The following link will take you straight to our project folder where you can drag and drop your files.

${globalscapeUrl}

You will need to log in using your @ets.alixpartners.com account which you should have received the details of in earlier emails.

Kind regards,
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateEtsPowerBiEmbedMail = (
  userName: string,
  codeName: string
) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: Deploy Power BI Embedded capacity for ${codeName} environment`;
  const body: string = `Dear ETS,

Please deploy Power BI Embedded capacity to the ${codeName} ToolBuilder environment. I understand that this will cost an additional $700 per month.

The url to access this environment is: ${window.location.hostname}

ETS please:
- Run a ToolBuilder release to the ${codeName} stage with the correct release variable set for this service
- A full list of of Release Variables can be found here on Confluence https://alixpartners.atlassian.net/wiki/spaces/CDO/pages/878477843/3.1.+Environment+setup

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateCostsDetails = (userName: string, codeName: string) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: Question about costs for ${codeName} environment`;
  const body: string = `Dear ETS,

Please consider my costs related question below:




The url to access this environment is: ${window.location.hostname}

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateEtsTableauEmbedMail = (
  userName: string,
  codeName: string
) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: Confirmation of approvers for ${codeName} environment`;
  const body: string = `Dear ETS,

The environment url is: ${window.location.hostname}

Please confirm who is my Approvers group as I need to publish reports. The specific AD group name is Sec_${codeName}_AZ_EL

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateEtPowerBiProLicenseMail = (userName: string) => {
  const to: string = 'Helpdesk@alixpartners.com';
  const subject: string = 'Request for Power BI Pro license allocation';
  const body: string = `Dear Helpdesk,

Please allocate a Power BI Pro License to my AD account. I require this to work with the ToolBuilder which requires I use Power BI Desktop and I may publish to Power BI Online. I
understand this to be a $0 cost license.

NOTE: This is a request for a Power BI Pro LICENSE not an install of the Power BI Desktop software, unless specified I do not require the software installing.

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};

export const generateGlobalscapeRequestMail = (
  userName: string,
  codeName: string
) => {
  const to: string = 'it-ccsi-request@alixpartners.com';
  const subject: string = `AlixIP ToolBuilder: Configure GlobalScape for ${codeName} environment`;
  const body: string = `Dear ETS,

Please configure GlobalScape for the ${codeName} ToolBuilder environment.

The url to access this environment is: ${window.location.hostname}

Many thanks.
${userName}
`;

  return mailtoLink({ to, subject, body });
};
